/**
 * useWakeWord — Lokaler On-Device Wake-Word-Listener (#1390, STANDBY-Hälfte).
 *
 * openWakeWord-Inferenz-Kette zu 100 % IM BROWSER (onnxruntime-web, WASM):
 *   Mic-PCM (AudioWorklet) → Resample 16 kHz → Chunks à 1280 Samples (80 ms)
 *   → melspectrogram.onnx [1,1280]→[1,1,5,32] (Transform x/10+2)
 *   → embedding_model.onnx (Sliding-Window 76 Mel-Frames) [1,76,32,1]→[1,1,1,96]
 *   → Wake-Modell [1,16,96]→Score (Sigmoid). Score ≥ threshold → onWake().
 *
 * DATENSCHUTZ (AK2): In diesem Hook existiert KEIN WebSocket und KEIN
 * HTTP-Upload — kein einziges Audio-Byte verlässt das Gerät. Die einzigen
 * Netz-Zugriffe sind die EINMALIGEN fetches der lokal gehosteten Modell-/
 * WASM-Assets unter /wake-models/ (same-origin, statisch).
 *
 * Phase 1 (#1390): Platzhalter-Modell hey_jarvis_v0.1.onnx (vortrainiert,
 * openWakeWord v0.5.1). Phase 2: trainiertes hey_flow.onnx ablegen +
 * voice_wake_model_file umstellen — dieser Hook bleibt unverändert.
 *
 * One-Shot: Nach einer Erkennung stoppt der Listener sich selbst (Mic wird
 * freigegeben, BEVOR die Voice-Session ihr getUserMedia öffnet) und feuert
 * onWake() genau einmal. Re-Arm macht der Aufrufer über `enabled`.
 *
 * @since v2.649.0 (#1390)
 */
import { useCallback, useEffect, useRef, useState } from 'react';

const TARGET_RATE = 16000;
const CHUNK_SAMPLES = 1280;        // 80 ms @16 kHz (openWakeWord-Konvention)
const MEL_FRAMES_WINDOW = 76;      // Embedding-Fenster (~775 ms)
const MEL_BINS = 32;
const EMB_WINDOW = 16;             // Wake-Modell-Fenster (16 Embeddings)
const EMB_DIM = 96;
const CONSECUTIVE_HITS = 2;        // 2 Chunks (160 ms) über Schwelle → weniger Fehlalarme
const MODEL_FILE_RE = /^[\w.-]+\.onnx$/;

const WORKLET_CODE = `
class WeshipWakeCapture extends AudioWorkletProcessor {
  process(inputs) {
    const ch = inputs[0] && inputs[0][0];
    if (ch && ch.length) this.port.postMessage(new Float32Array(ch));
    return true;
  }
}
registerProcessor('weship-wake-capture', WeshipWakeCapture);
`;

// ONNX-Sessions sind teuer — einmal pro Modell-Datei laden, dann wiederverwenden
// (Standby↔Active-Zyklen erzeugen den Hook-Lebenszyklus mehrfach pro Minute).
let _ortModulePromise = null;
const _sessionCache = new Map(); // modelFile -> Promise<{ort, mel, emb, wake}>

function _loadOrt() {
  if (!_ortModulePromise) {
    // WICHTIG: wasm-only-Subpackage — das Default-Package (ort.mjs, webgpu/jsep)
    // fordert zur Laufzeit ort-wasm-simd-threaded.jsep.mjs an, die wir nicht
    // self-hosten → 404 → model_load. Das wasm-Bundle nutzt exakt die Dateien
    // in /wake-models/ort/.
    _ortModulePromise = import('onnxruntime-web/wasm').then((ort) => {
      ort.env.wasm.wasmPaths = '/wake-models/ort/';
      ort.env.wasm.numThreads = 1; // kein COOP/COEP-Zwang (iPad-Safari)
      return ort;
    });
  }
  return _ortModulePromise;
}

function _loadSessions(modelFile) {
  if (!_sessionCache.has(modelFile)) {
    _sessionCache.set(modelFile, (async () => {
      const ort = await _loadOrt();
      const opts = { executionProviders: ['wasm'] };
      const [mel, emb, wake] = await Promise.all([
        ort.InferenceSession.create('/wake-models/melspectrogram.onnx', opts),
        ort.InferenceSession.create('/wake-models/embedding_model.onnx', opts),
        ort.InferenceSession.create(`/wake-models/${modelFile}`, opts),
      ]);
      return { ort, mel, emb, wake };
    })().catch((e) => { _sessionCache.delete(modelFile); throw e; }));
  }
  return _sessionCache.get(modelFile);
}

/**
 * @param {Object} opts
 * @param {boolean} opts.enabled    Listener aktiv (STANDBY)
 * @param {string}  opts.modelFile  ONNX-Datei unter /wake-models/
 * @param {number}  opts.threshold  Erkennungs-Schwelle 0–1
 * @param {Function} opts.onWake    genau 1× pro Erkennung (Listener stoppt vorher selbst)
 * @returns {{ status: 'off'|'loading'|'listening'|'error', errorCode: string|null }}
 */
export function useWakeWord({ enabled, modelFile, threshold, onWake }) {
  const [status, setStatus] = useState('off');
  const [errorCode, setErrorCode] = useState(null);
  const onWakeRef = useRef(onWake);
  onWakeRef.current = onWake;

  const runningRef = useRef(null); // Abbruch-Token des aktiven Listeners

  const stopRef = useRef(() => {});

  const startListener = useCallback(async (file, thr) => {
    const token = { stopped: false, stream: null, ctx: null, worklet: null };
    runningRef.current = token;
    setErrorCode(null);
    setStatus('loading');

    if (!MODEL_FILE_RE.test(file)) {
      setStatus('error');
      setErrorCode('model_invalid');
      return;
    }

    let sessions;
    try {
      sessions = await _loadSessions(file);
    } catch {
      if (token.stopped) return;
      setStatus('error');
      setErrorCode('model_load');
      return;
    }
    if (token.stopped) return;

    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
      });
    } catch {
      if (token.stopped) return;
      setStatus('error');
      setErrorCode('mic_denied');
      return;
    }
    if (token.stopped) { stream.getTracks().forEach((t) => t.stop()); return; }
    token.stream = stream;

    let ctx;
    try {
      ctx = new (window.AudioContext || window.webkitAudioContext)();
      token.ctx = ctx;
      const blobUrl = URL.createObjectURL(new Blob([WORKLET_CODE], { type: 'application/javascript' }));
      await ctx.audioWorklet.addModule(blobUrl);
      URL.revokeObjectURL(blobUrl);
      if (token.stopped) return;
      const src = ctx.createMediaStreamSource(stream);
      const node = new AudioWorkletNode(ctx, 'weship-wake-capture');
      token.worklet = node;
      src.connect(node);

      const { ort, mel, emb, wake } = sessions;
      const ratio = ctx.sampleRate / TARGET_RATE;
      const needSrc = Math.ceil(CHUNK_SAMPLES * ratio);
      let srcBuf = new Float32Array(0);
      const melBuf = [];   // Array<Float32Array(32)> — Sliding-Window der Mel-Frames
      const embBuf = [];   // Array<Float32Array(96)> — letzte 16 Embeddings
      let hits = 0;
      let busy = false;

      const processChunk = async (chunk16k) => {
        // 1) Melspectrogram: [1,1280] → [1,1,5,32], openWakeWord-Transform x/10+2
        const melOut = await mel.run({ [mel.inputNames[0]]: new ort.Tensor('float32', chunk16k, [1, CHUNK_SAMPLES]) });
        const melData = melOut[mel.outputNames[0]].data;
        const nFrames = melData.length / MEL_BINS;
        for (let f = 0; f < nFrames; f++) {
          const frame = new Float32Array(MEL_BINS);
          for (let b = 0; b < MEL_BINS; b++) frame[b] = melData[f * MEL_BINS + b] / 10 + 2;
          melBuf.push(frame);
        }
        while (melBuf.length > MEL_FRAMES_WINDOW) melBuf.shift();
        if (melBuf.length < MEL_FRAMES_WINDOW) return null;

        // 2) Embedding: [1,76,32,1] → [1,1,1,96]
        const embIn = new Float32Array(MEL_FRAMES_WINDOW * MEL_BINS);
        for (let f = 0; f < MEL_FRAMES_WINDOW; f++) embIn.set(melBuf[f], f * MEL_BINS);
        const embOut = await emb.run({ [emb.inputNames[0]]: new ort.Tensor('float32', embIn, [1, MEL_FRAMES_WINDOW, MEL_BINS, 1]) });
        embBuf.push(Float32Array.from(embOut[emb.outputNames[0]].data));
        while (embBuf.length > EMB_WINDOW) embBuf.shift();
        if (embBuf.length < EMB_WINDOW) return null;

        // 3) Wake-Modell: [1,16,96] → Score (Sigmoid bereits im Modell)
        const wakeIn = new Float32Array(EMB_WINDOW * EMB_DIM);
        for (let i = 0; i < EMB_WINDOW; i++) wakeIn.set(embBuf[i], i * EMB_DIM);
        const wakeOut = await wake.run({ [wake.inputNames[0]]: new ort.Tensor('float32', wakeIn, [1, EMB_WINDOW, EMB_DIM]) });
        return Number(wakeOut[wake.outputNames[0]].data[0]);
      };

      node.port.onmessage = (e) => {
        if (token.stopped) return;
        const frame = e.data;
        const merged = new Float32Array(srcBuf.length + frame.length);
        merged.set(srcBuf, 0);
        merged.set(frame, srcBuf.length);
        srcBuf = merged;
        if (srcBuf.length < needSrc || busy) return;

        // Resample (linear) → exakt 1280 Samples @16 kHz
        const chunk = new Float32Array(CHUNK_SAMPLES);
        for (let i = 0; i < CHUNK_SAMPLES; i++) {
          const pos = i * ratio;
          const i0 = Math.floor(pos);
          const i1 = Math.min(i0 + 1, srcBuf.length - 1);
          chunk[i] = srcBuf[i0] + (srcBuf[i1] - srcBuf[i0]) * (pos - i0);
        }
        srcBuf = srcBuf.slice(Math.floor(CHUNK_SAMPLES * ratio));

        busy = true;
        processChunk(chunk).then((score) => {
          busy = false;
          if (token.stopped || score === null) return;
          if (score >= thr) {
            hits++;
            if (hits >= CONSECUTIVE_HITS) {
              // One-Shot: Mic SOFORT freigeben, dann aufwecken.
              stopRef.current();
              setStatus('off');
              onWakeRef.current?.();
            }
          } else {
            hits = 0;
          }
        }).catch(() => { busy = false; });
      };

      setStatus('listening');
    } catch {
      if (!token.stopped) { setStatus('error'); setErrorCode('audio_unsupported'); }
      stopRef.current();
    }
  }, []);

  stopRef.current = useCallback(() => {
    const token = runningRef.current;
    if (!token) return;
    token.stopped = true;
    runningRef.current = null;
    if (token.worklet) { try { token.worklet.port.onmessage = null; token.worklet.disconnect(); } catch { /* noop */ } }
    if (token.stream) { try { token.stream.getTracks().forEach((t) => t.stop()); } catch { /* noop */ } }
    if (token.ctx) { token.ctx.close().catch(() => {}); }
  }, []);

  useEffect(() => {
    if (enabled && modelFile) {
      startListener(modelFile, Number(threshold) || 0.5);
      return () => { stopRef.current(); setStatus('off'); };
    }
    stopRef.current();
    setStatus('off');
    return undefined;
  }, [enabled, modelFile, threshold, startListener]);

  return { status, errorCode };
}
